import React, { useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppSelector,
  useAppDispatch,
} from '../../../../../app/store/configureStore';
import { TableRow } from './TableRow';
import { useTableActions } from '../../hooks/useTableActions';
import {
  selectRestaurantTables,
  selectTableStatus,
} from '../../store/selectors';
import { fetchRestaurantTableListAsync } from '../../store/tableSlice';
import EmptyScreen from '../../../../../app/components/EmptyScreen';
import LoadingComponent from '../../../../../app/layout/LoadingComponent';
import { ConfirmDialog } from '../../../../../app/components/ConfirmDialog';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import { TableHeader } from './TableHeader';
import { RestaurantTable } from '../../types';

export const TableList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { restaurantId } = useParams<{ restaurantId: string }>();

  const tables = useAppSelector(selectRestaurantTables);
  const status = useAppSelector(selectTableStatus);
  const { loading, target, handleDelete } = useTableActions(restaurantId);

  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [tableToDelete, setTableToDelete] = React.useState<string | null>(null);

  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchRestaurantTableListAsync(restaurantId));
    }
  }, [dispatch, restaurantId]);

  const handleEdit = React.useCallback(
    (table: RestaurantTable) => {
      navigate(
        `/manager/restaurants/${restaurantId}/restaurant-tables/${table.id}/edit?tableNumber=${table.tableNumber}`
      );
    },
    [navigate, restaurantId]
  );

  const handleQrCode = React.useCallback(
    (table: RestaurantTable) => {
      navigate(
        `/manager/restaurants/${restaurantId}/restaurant-tables/qr-code?tableNumber=${table.tableNumber}`
      );
    },
    [navigate, restaurantId]
  );

  const handleCreate = React.useCallback(() => {
    navigate(`/manager/restaurants/${restaurantId}/restaurant-tables/create`);
  }, [navigate, restaurantId]);

  const handleDeleteClick = React.useCallback((id: string) => {
    setTableToDelete(id);
    setOpenConfirm(true);
  }, []);

  const handleConfirmDelete = React.useCallback(async () => {
    if (tableToDelete) {
      setOpenConfirm(false);
      await handleDelete(tableToDelete);
      setTableToDelete(null);
    }
  }, [tableToDelete, handleDelete]);

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <LoadingComponent
          header={
            <ListTitleBox
              title={t('table.list.title')}
              onPrimaryClick={handleCreate}
            />
          }
        />
      );
    }

    if (!tables || tables.length === 0) {
      return (
        <EmptyScreen
          title={t('table.list.title')}
          emptyText={t('table.list.empty')}
          onPrimaryClick={handleCreate}
        />
      );
    }

    return (
      <>
        <ListTitleBox
          title={t('table.list.title')}
          onPrimaryClick={handleCreate}
        />
        <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
          <Table aria-label={t('table.list.title')}>
            <TableHead>
              <TableHeader />
            </TableHead>
            <TableBody>
              {tables.map((table, index) => (
                <TableRow
                  key={table.id}
                  table={table}
                  restaurantId={restaurantId}
                  index={index}
                  loading={loading}
                  target={target}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onQrCode={handleQrCode}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <>
      {renderContent()}
      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title={t('table.delete.confirm.title')}
        message={t('table.delete.confirm.message')}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
